import { Tabs } from "antd";
import { t } from "@lingui/macro";
import {
	getNotificationsAsync,
	readAllNotificationsAsync,
	readNotificationAsync,
	selectNotifications,
	selectTotalUnreadNotifications,
} from "./redux/notification.slice";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";
import { Col, Row, Button } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { translate } from "utils";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { NOTIFICATION_TYPE } from "constants/constants";
import { APP_PATH } from "constants/path";
import { Notification } from "./type";
import { selectLang } from "store/lang/langSlice";
import { Translation } from "types";

interface IDictionary {
	[index: string]: Array<Map<string, string>>;
}

export const transCarNames = (notiData: any, lang: string) => {
	if (!notiData.carTranslations) return;
	let textValue = "";
	notiData.carTranslations.forEach((el: Translation, idx: number) => {
		const textTrans: string = el[lang];
		textValue += `${textTrans} ${
			idx !== notiData.carTranslations.length - 1 ? ", " : ""
		}`;
	});

	return textValue;
};

export const NOTIFICATION_FIELDS = {
	"Customer has just created a new appointment": [
		{
			displayName: "Booking ID",
			path: "bookingId",
		},
		{
			displayName: "Customer",
			path: "customerName",
		},
		{
			displayName: "Time",
			path: {
				time: "timeSlot",
				date: "date",
			},
		},
		{
			displayName: "Showroom",
			path: "showroom",
		},
	],
	"Customer has just canceled an appointment": [
		{
			displayName: "Booking ID",
			path: "bookingId",
		},
		{
			displayName: "Customer",
			path: "customerName",
		},
		{
			displayName: "Time",
			path: {
				time: "timeSlot",
				date: "date",
			},
		},
		{
			displayName: "Showroom",
			path: "showroom",
		},
	],
	"Merchant just added a car": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "carName",
			transCarNames,
		},
	],
	"Merchant just edited information of a car": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "carName",
			transCarNames,
		},
	],
	"Merchant has just requested a new car": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "requestName",
			transCarNames,
		},
	],
	"Merchant has just canceled a car request": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "requestName",
			transCarNames,
		},
	],
	"Merchant has just requested a new service": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Request ID",
			path: "requestName",
			transCarNames,
		},
	],
	"Merchant has just canceled a service request": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Request ID",
			path: "requestName",
		},
	],
	"You have a new contact support": [
		{
			displayName: "Customer",
			path: "customerName",
		},
		{
			displayName: "Purchased",
			path: "purchaseOrderId",
		},
	],
	"Customer has just registered to become a collaborator": [
		{
			displayName: "Customer",
			path: "customerName",
		},
	],
	"Customer has just created a new rental car booking": [
		{
			displayName: "Booking ID",
			path: "bookingId",
		},
		{
			displayName: "Customer",
			path: "customerName",
		},
		{
			displayName: "Time",
			customSlot: (notiData: any) => (
				<span>
					{notiData.pickupDate} - {notiData.returnDate}
				</span>
			),
		},
	],
	"Merchant just edited information of a car for rent": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "carName",
			transCarNames,
		},
	],
	"Merchant has just added a car for rent": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Title car",
			path: "carName",
			transCarNames,
		},
	],
	"Customer has just canceled a Rental Car booking": [
		{
			displayName: "Booking ID",
			path: "bookingId",
		},
		{
			displayName: "Customer",
			path: "customerName",
		},
	],
	"Rental car booking is completed": [
		{
			displayName: "Purchase ID",
			path: "purchaseOrderId",
		},
		{
			displayName: "Customer",
			path: "customerName",
		},
	],
	"Merchant has just added a new car care service": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Service name",
			path: "service",
		},
	],
	"Merchant has just edited information of a car care": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Service name",
			path: "service",
		},
	],
	"Merchant just requested new post for car care service": [],

	"Merchant has just create new voucher": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Voucher",
		},
	],
	"Merchant has just edited voucher": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Voucher",
		},
	],
	"Store Information Has Been Updated.": [
		{
			displayName: "Merchant",
			path: "merchantName",
		},
		{
			displayName: "Store",
			path: "carStoreName",
		},
	],
	"Customer has requested car loan": [
		{
			displayName: "Request ID",
			path: "formRequestId",
		}
	],
} as unknown as IDictionary;

const NUMBER_OF_NOTIFICATIONS = 10;

export default function Notifications() {
	const dispatch = useDispatch();
	const notifications = useSelector(selectNotifications);
	const totalUnreadNotifications = useSelector(selectTotalUnreadNotifications);
	const [loading, setLoading] = useState(true);
	const [skip, setSkip] = useState(0);
	const navigate = useNavigate();
	const lang = useSelector(selectLang);

	useEffect(() => {
		setLoading(true);
		dispatch(
			getNotificationsAsync({
				query: {
					skip: 0,
					take: NUMBER_OF_NOTIFICATIONS,
				},
				isNewNotifications: true,
				returnResult: (result: boolean) => {
					if (result) {
						setLoading(false);
					}
				},
			}),
		);
	}, [totalUnreadNotifications]);

	useEffect(() => {
		if (notifications.length < skip) {
			return;
		}
		const listNotiDiv = document.querySelector("#list-noti");
		function loadMore(event: any) {
			const el = event.target;
			if (el.scrollTop + el.clientHeight === el.scrollHeight) {
				setLoading(true);
				setSkip(skip + NUMBER_OF_NOTIFICATIONS);
				setTimeout(() => {
					dispatch(
						getNotificationsAsync({
							query: {
								skip: skip + NUMBER_OF_NOTIFICATIONS,
								take: NUMBER_OF_NOTIFICATIONS,
							},
							returnResult: (result: boolean) => {
								if (result) {
									setLoading(false);
								}
							},
						}),
					);
				}, 500);
			}
		}
		listNotiDiv?.addEventListener("scroll", loadMore);
		return () => {
			listNotiDiv?.removeEventListener("scroll", loadMore);
		};
	}, [skip, notifications]);

	const formatDate = (date: string) => {
		const parts = date.split("-");
		return parts[2] + "/" + parts[1] + "/" + parts[0];
	};

	return (
		<div>
			<Tabs className="bg-white w-max drop-shadow-xl border">
				<Tabs.TabPane tab={`${t`Notifications`}`} key="item-1">
					<div
						className="px-5 pb-5 max-h-[540px] w-[500px] overflow-y-auto"
						id="list-noti"
					>
						{loading && !notifications.length ? (
							<div className="flex justify-center">
								<LoadingOutlined className="text-blue-500" />
							</div>
						) : null}
						{notifications.length ? (
							notifications.map((noti: Notification, index: number) => {
								const getNotificationText = (title: string) =>
									translate(title.replace(/\.$/, ""));
								const text = getNotificationText(noti.title);
								return (
									NOTIFICATION_FIELDS[noti.title as unknown as string] && (
										<div
											key={index}
											onClick={() => {
												switch (noti.type) {
													case NOTIFICATION_TYPE.created_car:
														navigate(`/crm/merchants/cars/${noti.data.carId}`);
														break;
													case NOTIFICATION_TYPE.merchant_just_edited_information_of_a_car:
														navigate(`/crm/merchants/cars/${noti.data.carId}`);
														break;
													case NOTIFICATION_TYPE.staff_new_test_drive:
														navigate(
															`transaction-appointment/${noti.data.bookingId}`,
														);
														break;
													case NOTIFICATION_TYPE.staff_cancel_test_by_customer:
														navigate(
															`transaction-appointment/${noti.data.bookingId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_add_car_request:
														navigate(
															`/request-services/transaction-merchant-request/${noti.data.merchantRequestId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_cancel_car_request:
														navigate(
															`/request-services/transaction-merchant-request/${noti.data.merchantRequestId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_add_service_request:
														navigate(
															`/request-services/transaction-merchant-request/${noti.data.merchantRequestId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_cancel_service_request:
														navigate(
															`/request-services/transaction-merchant-request/${noti.data.merchantRequestId}`,
														);
														break;
													case NOTIFICATION_TYPE.customer_create_new_customer_request:
														navigate(
															`${APP_PATH.contactSupports}/${noti.data.id}`,
														);
														break;
													case NOTIFICATION_TYPE.customer_register_collaborator:
														navigate(
															`${APP_PATH.collaborators}/${noti.data.formId}`,
														);
														break;
													case NOTIFICATION_TYPE.staff_new_rent_appointment:
														navigate(
															`${APP_PATH.transactionAppointment}/${noti.data.bookingId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_updated_rent_car:
														navigate(`${APP_PATH.cars}/${noti.data.carId}`);
														break;
													case NOTIFICATION_TYPE.merchant_created_rent_car:
														navigate(`${APP_PATH.cars}/${noti.data.carId}`);
														break;
													case NOTIFICATION_TYPE.staff_customer_cancel_rent_appointment:
														navigate(
															`${APP_PATH.transactionAppointment}/${noti.data.bookingId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_completed_rental_purchase:
														navigate(
															`${APP_PATH.transactionPurchase}/${noti.data.purchaseOrderId}`,
														);
														break;
													case NOTIFICATION_TYPE.car_wash_merchant_created:
														navigate(
															`${APP_PATH.carCares}/${noti.data?.carWashId}`,
														);
														break;
													case NOTIFICATION_TYPE.car_wash_merchant_updated:
														navigate(
															`${APP_PATH.carCares}/${noti.data?.carWashId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_created_car_wash_post_to_staffs:
														navigate(
															`${APP_PATH.communityPosts}/${noti.data?.postId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_created_voucher_to_staffs:
														navigate(
															`${APP_PATH.loyaltyCustomerVouchersOfMerchant}/${noti.data?.voucherId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_edited_voucher_to_staffs:
														navigate(
															`${APP_PATH.loyaltyCustomerVouchersOfMerchant}/${noti.data?.voucherId}`,
														);
														break;
													case NOTIFICATION_TYPE.merchant_update_car_store_to_staffs:
														navigate(
															`${APP_PATH.stores}/${noti.data?.carStoreId}`,
														);
														break;
													case NOTIFICATION_TYPE.car_loan_request_created_to_staff:
														navigate(
															`${APP_PATH.loanApplications}/${noti.data?.formRequestId}`,
														);
												}
												if (!noti.read) {
													dispatch(
														readNotificationAsync({
															id: noti.id,
														}),
													);
												}
											}}
											className="cursor-pointer"
										>
											<Row className="w-full pl-2 py-2 hover:bg-slate-100">
												<Col span={16}>
													<div>
														<p
															className={`break-words ${
																noti.read
																	? "text-sky-700"
																	: "font-bold text-sky-600"
															}`}
														>
															{text}
														</p>
														<div
															className={`text-xs ${
																noti.read ? "text-stone-500" : "text-stone-900"
															}`}
														>
															{(() => {
																if (
																	noti.type ===
																	NOTIFICATION_TYPE.merchant_update_car_store_to_staffs
																) {
																	return (
																		<p>
																			{translate(
																				noti.body?.split("\n")[0] || "",
																			)}
																		</p>
																	);
																}
																return null;
															})()}

															{NOTIFICATION_FIELDS[
																noti.title as unknown as string
															].length
																? NOTIFICATION_FIELDS[
																		noti.title as unknown as string
																  ]?.map((element: any) => {
																		let textValue = "";
																		if (
																			element.displayName === "Time" &&
																			element.path &&
																			!element.customSlot
																		) {
																			textValue =
																				translate(
																					_.get(noti.data, element.path.time),
																				) +
																				` ${translate("on")} ` +
																				formatDate(
																					_.get(noti.data, element.path.date),
																				);
																		} else if (
																			element.transCarNames &&
																			noti.data.carTranslations
																		) {
																			textValue = element.transCarNames(
																				noti.data,
																				lang,
																			);
																		} else if (
																			noti.data.voucherNameTranslation &&
																			element.displayName === "Voucher"
																		) {
																			textValue =
																				noti.data.voucherNameTranslation[lang];
																		} else if (element.customSlot) {
																			textValue = element.customSlot(noti.data);
																		} else {
																			textValue = _.get(
																				noti.data,
																				element.path,
																			);
																		}

																		return (
																			<p>
																				{`${translate(element.displayName)}: `}
																				<span className="font-semibold">
																					{textValue}
																				</span>
																			</p>
																		);
																  })
																: translate(noti.body || "")}
														</div>
													</div>
												</Col>
												<Col span={7}>
													<p
														className={`text-xs pt-1 italic ${
															noti.read ? "text-stone-500" : "text-stone-900"
														}`}
													>
														{moment(noti.createdAt).format("HH:mm DD/MM/YYYY")}
													</p>
												</Col>
												<Col span={1}>
													<p
														className="text-sky-600"
														style={{ fontSize: "10px" }}
													>
														{noti.read ? "" : "\u2B24"}
													</p>
												</Col>
											</Row>
										</div>
									)
								);
							})
						) : !loading ? (
							<p className="text-center opacity-40">{t`No data`}</p>
						) : null}
						{loading && notifications.length ? (
							<div className="flex justify-center">
								<LoadingOutlined className="text-blue-500" />
							</div>
						) : null}
						{totalUnreadNotifications > 0 && (
							<Button
								onClick={() => {
									dispatch(readAllNotificationsAsync({}));
								}}
								icon={<CheckOutlined className="font-medium text-sky-900" />}
								type="text"
								className="fixed bottom-5 right-6 flex items-center font-medium text-sky-900 rounded-lg hover:bg-blue-200 bg-blue-50"
							>
								{t`Read all`}
							</Button>
						)}
					</div>
				</Tabs.TabPane>
			</Tabs>
		</div>
	);
}
